import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setUser } from '../../../redux/features/authSlice';
import HeaderSection from '../../header/HeaderSection';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { toast } from 'react-toastify';
import { getProfileRoleLabel, getWorkerProfileRole } from '../../../utils/profileRoleUtils';

const WorkerProfileView = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [address, setAddress] = useState('');
  
  // Worker preferences
  const [workerPreferences, setWorkerPreferences] = useState(() => {
    if (typeof window === 'undefined') return { useAI: false, enableParkingReminder: false };
    const saved = localStorage.getItem('workerPreferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { useAI: false, enableParkingReminder: false };
      }
    }
    return { useAI: false, enableParkingReminder: false };
  });
  
  useEffect(() => {
    // Get profile data from localStorage and Redux
    const storedProfile = localStorage.getItem('profile');
    
    if (storedProfile) {
      try {
        const parsedProfile = JSON.parse(storedProfile);
        setProfile(parsedProfile);
        
        // Set fields from profile
        if (parsedProfile?.result) {
          setCompanyName(parsedProfile.result.companyName || '');
          setUserRole(getWorkerProfileRole(parsedProfile.result));
          setFirstName(parsedProfile.result.firstName || '');
          setLastName(parsedProfile.result.lastName || '');
          setEmail(parsedProfile.result.email || '');
          setPhone(parsedProfile.result.phone || '');
          setSpecialization(parsedProfile.result.specialization || '');
          setAddress(parsedProfile.result.address || '');
        }
      } catch (error) {
        console.error('Error parsing profile:', error);
      }
    } else if (user?.result) {
      // Use Redux user data if localStorage doesn't have profile
      setCompanyName(user.result.companyName || '');
      setUserRole(getWorkerProfileRole(user.result));
      setFirstName(user.result.firstName || '');
      setLastName(user.result.lastName || '');
      setEmail(user.result.email || '');
      setPhone(user.result.phone || '');
      setSpecialization(user.result.specialization || '');
      setAddress(user.result.address || '');
    }
    
    setLoading(false);
  }, [user]);
  
  const handleSave = async () => {
    if (!profile && !user?.result) return;
    
    setSaving(true);
    
    try {
      const token = user?.token || profile?.token || localStorage.getItem('token');
      const workerId = user?.result?._id || profile?.result?._id;
      
      if (!token || !workerId) {
        toast.error('Nedostaju podaci za autentifikaciju');
        setSaving(false);
        return;
      }
      
      // Update worker via API
      const response = await axios.patch(
        `${API_BASE_URL}/api/worker/${workerId}`,
        {
          firstName,
          lastName,
          email,
          phone,
          specialization,
          address
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      // Update localStorage profile
      let updatedProfile = profile || { result: {} };
      if (!updatedProfile.result) {
        updatedProfile.result = {};
      }
      
      updatedProfile.result.firstName = firstName;
      updatedProfile.result.lastName = lastName;
      updatedProfile.result.email = email;
      updatedProfile.result.phone = phone;
      updatedProfile.result.specialization = specialization;
      updatedProfile.result.address = address;
      
      localStorage.setItem('profile', JSON.stringify(updatedProfile));
      
      // Dispatch update to Redux
      dispatch(setUser(updatedProfile));
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success('Profil je uspešno ažuriran');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Greška pri ažuriranju profila');
    } finally {
      setSaving(false);
    }
  };
  
  const onAdd = () => {};
  const onRead = () => {};
  const onUpdate = () => {};
  const onDelete = () => {};
  
  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <HeaderSection 
        title="Moj Profil" 
        onAdd={onAdd}
        onRead={onRead} 
        onUpdate={onUpdate} 
        onDelete={onDelete} 
      />
      
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 mt-4 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold text-white mb-6">Moj Profil</h2>
        
        <div className="space-y-6">
          {/* Company Name (read-only) */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-1">
              {t('profile.companyName')}
            </label>
            <input
              type="text"
              id="companyName"
              readOnly
              className="w-full p-2 bg-gray-600 border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
              value={companyName}
            />
          </div>

          {/* Role (read-only) */}
          <div>
            <label htmlFor="userRole" className="block text-sm font-medium text-gray-300 mb-1">
              {t('profile.role')}
            </label>
            <input
              type="text"
              id="userRole"
              readOnly
              className="w-full p-2 bg-gray-600 border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
              value={getProfileRoleLabel(t, userRole)}
            />
          </div>

          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
              Ime
            </label>
            <input
              type="text"
              id="firstName"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ime"
            />
          </div>
          
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
              Prezime
            </label>
            <input
              type="text"
              id="lastName"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Prezime"
            />
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
            />
          </div>
          
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              id="phone"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Telefon"
            />
          </div>
          
          {/* Specialization */}
          <div>
            <label htmlFor="specialization" className="block text-sm font-medium text-gray-300 mb-1">
              Specijalizacija
            </label>
            <input
              type="text"
              id="specialization"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value)}
              placeholder="Specijalizacija"
            />
          </div>
          
          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-1">
              Adresa
            </label>
            <textarea
              id="address"
              rows="3"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Adresa"
            />
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Čuvanje...' : 'Sačuvaj'}
          </button>
        </div>
        
        {saved && (
          <div className="mt-4 p-2 bg-green-600 text-white rounded-md">
            Profil je uspešno sačuvan
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerProfileView;

